import React, { useEffect, useState } from "react";
import StaffLayout from "@/components/StaffLayout";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, Legend, Cell, FunnelChart, Funnel, LabelList,
} from "recharts";
import {
  BarChart3, DollarSign, TrendingUp, Users, CreditCard, ShoppingBag, Mail, Package,
  Info, Download, AlertTriangle, Filter,
} from "lucide-react";

const RANGE_OPTIONS = [
  { v: "7d",  l: "7 días" },
  { v: "30d", l: "30 días" },
  { v: "90d", l: "90 días" },
  { v: "180d", l: "180 días" },
  { v: "ytd", l: "YTD" },
  { v: "all", l: "Todo" },
];

const TABS = [
  { v: "overview", l: "Overview", testid: "reports-tab-overview" },
  { v: "revenue", l: "Revenue", testid: "reports-tab-revenue" },
  { v: "cohorts", l: "Cohorts", testid: "reports-tab-cohorts" },
  { v: "ltv", l: "LTV", testid: "reports-tab-ltv" },
  { v: "funnel", l: "Funnel", testid: "reports-tab-funnel" },
  { v: "retention", l: "Retención", testid: "reports-tab-retention" },
];

function fmtMxn(n) { return "$" + Number(n || 0).toLocaleString("es-MX", { maximumFractionDigits: 0 }); }
function fmtNum(n) { return Number(n || 0).toLocaleString("es-MX"); }
function fmtPct(n) { return `${n || 0}%`; }
function pctColor(v) { return v > 0 ? "text-emerald-400" : v < 0 ? "text-destructive" : "text-gray-400"; }

const CHART_MARGIN = { top: 5, right: 12, left: 12, bottom: 5 };

export default function SuperadminReports() {
  const { user } = useAuth();
  const superadmin = user?.roles?.includes("superadmin_dcc");
  const [tab, setTab] = useState("overview");
  const [range, setRange] = useState("30d");

  const download = async (reportType, params = {}, fmt = "csv") => {
    try {
      const r = await api.post("/admin/reports/export", { report_type: reportType, params: { range, ...params }, format: fmt });
      // fetch and blob-download
      const resp = await api.get(r.data.download_url.replace("/api", ""), { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `dcc-${reportType}-${range}.${fmt}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exportado ${r.data.rows_exported} filas (${(r.data.size_bytes / 1024).toFixed(1)} KB)`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <StaffLayout user={user} superadmin={superadmin}>
      <div>
        <div className="flex items-end justify-between mb-4 flex-wrap gap-3">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary font-semibold">Fase 14.1 · Growth &amp; Analytics</p>
            <h1 className="mt-2 text-3xl font-bold text-white font-display">Reportería ejecutiva</h1>
            <p className="mt-1 text-sm text-gray-400">
              Vista consolidada del negocio con drill-down. Cache 5 min.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-1 border border-gray-800 rounded-md p-1 bg-[#111827]">
              {RANGE_OPTIONS.map((r) => (
                <button
                  key={r.v}
                  onClick={() => setRange(r.v)}
                  data-testid={`range-${r.v}`}
                  className={`px-2 py-1 rounded text-xs font-semibold ${
                    range === r.v ? "bg-primary text-primary-foreground" : "text-gray-400 hover:text-white"
                  }`}
                >
                  {r.l}
                </button>
              ))}
            </div>
            <button
              onClick={() => download(tab, {}, "csv")}
              data-testid="reports-export-btn"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-800 text-white text-xs font-semibold hover:bg-white/5"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
            <button
              onClick={() => download(tab, {}, "xlsx")}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-gray-800 text-white text-xs font-semibold hover:bg-white/5"
            >
              <Download className="w-3.5 h-3.5" /> XLSX
            </button>
          </div>
        </div>

        {/* Synthetic data banner */}
        <div className="mb-4 p-3 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-start gap-2" data-testid="synthetic-banner">
          <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-amber-200">
            <strong>Datos sintéticos para demo</strong> — el dashboard incluye ~1000 órdenes, 400 suscripciones y 2000 deliveries de email generados con flag <code className="bg-black/30 px-1 rounded font-mono">is_synthetic=true</code>. Purgables via <code className="bg-black/30 px-1 rounded font-mono">DELETE /api/admin/reports/synthetic-data</code>.
          </p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-gray-800 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.v}
              onClick={() => setTab(t.v)}
              data-testid={t.testid}
              className={`px-4 py-2 text-sm font-semibold border-b-2 transition-colors ${
                tab === t.v ? "text-primary border-primary" : "text-gray-400 border-transparent hover:text-white"
              }`}
            >
              {t.l}
            </button>
          ))}
        </div>

        {tab === "overview" && <OverviewTab range={range} />}
        {tab === "revenue" && <RevenueTab range={range} />}
        {tab === "cohorts" && <CohortsTab />}
        {tab === "ltv" && <LTVTab />}
        {tab === "funnel" && <FunnelTab range={range} />}
        {tab === "retention" && <RetentionTab />}
      </div>
    </StaffLayout>
  );
}

// =========================================================================
// OVERVIEW
// =========================================================================
function OverviewTab({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/overview?range=${range}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [range]);
  if (loading || !data) return <Skeleton />;
  const r = data.revenue, s = data.subscriptions, o = data.orders, u = data.users, p = data.programs, e = data.email, part = data.partners;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <KpiCard testid="kpi-card-revenue"
          label="Revenue total" value={fmtMxn(r.total_mxn)} delta={r.growth_pct_vs_prev} icon={DollarSign}
          tooltip={`Total facturado (paid orders) en el rango vs periodo anterior. Rango ${data.range}.`}
        />
        <KpiCard testid="kpi-card-mrr"
          label="MRR" value={fmtMxn(s.mrr_mxn)} sub={`ARR ${fmtMxn(s.arr_mxn)}`} icon={CreditCard}
          tooltip="Monthly Recurring Revenue = suma de subs activas normalizada a mensual (annuales / 12)."
        />
        <KpiCard testid="kpi-card-orders" label="Órdenes" value={fmtNum(o.count)} sub={`AOV ${fmtMxn(o.aov_mxn)}`} icon={ShoppingBag}
          tooltip="Órdenes pagadas y ticket promedio en el rango."
        />
        <KpiCard testid="kpi-card-subs-active" label="Suscripciones activas" value={fmtNum(s.active)} sub={`+${s.new_last_30d} nuevas · -${s.canceled_last_30d} bajas 30d`} icon={CreditCard}
          tooltip="Suscripciones con status active/trialing/past_due."
        />
        <KpiCard testid="kpi-card-churn" label="Churn 30d" value={fmtPct(s.churn_rate_pct_last_30d)} icon={TrendingUp} accent
          tooltip="Cancelaciones últimos 30d / activas al inicio del periodo × 100."
        />
        <KpiCard testid="kpi-card-new-users" label="New users 30d" value={fmtNum(u.new_last_30d)} sub={`${fmtNum(u.active_last_30d)} activos`} icon={Users}
          tooltip="Usuarios con created_at en los últimos 30 días."
        />
        <KpiCard testid="kpi-card-email-open" label="Open rate email" value={fmtPct(e.avg_open_rate)} sub={`${fmtNum(e.campaigns_sent)} campañas`} icon={Mail}
          tooltip="Promedio de aperturas / enviadas en todas las campañas sent."
        />
        <KpiCard testid="kpi-card-refund" label="Refund rate" value={fmtPct(o.refund_rate_pct)} sub={`${fmtNum(o.refunds_count)} rembolsos`} icon={Package}
          tooltip="Total reembolsado / revenue del rango × 100."
        />
        <KpiCard testid="kpi-card-top-tenant" label="Top tenant" value={part.top_tenant?.slug || "—"} sub={part.top_tenant ? fmtMxn(part.top_tenant.mxn) : ""} icon={Users}
          tooltip="Partner con mayor revenue en el rango."
        />
        <KpiCard testid="kpi-card-msi-share" label="MSI share" value={fmtPct(o.msi_share_pct)} sub={`${fmtNum(o.msi_count)} MSI orders`} icon={CreditCard}
          tooltip="Órdenes con plan de meses sin intereses / total."
        />
      </div>

      {/* Revenue chart */}
      <div className="p-5 rounded-xl border border-gray-800 bg-[#111827]">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white">Revenue por día · {data.range}</h3>
          <span className="text-xs text-gray-500">{r.by_day.length} días con actividad</span>
        </div>
        <div style={{ width: "100%", height: 260 }} data-testid="revenue-chart">
          <ResponsiveContainer>
            <LineChart data={r.by_day} margin={CHART_MARGIN}>
              <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
              <XAxis dataKey="date" stroke="#6b7280" style={{ fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis stroke="#6b7280" style={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
              <Tooltip contentStyle={{ background: "#0B0F14", border: "1px solid #1f2937", color: "#fff", fontSize: 12 }}
                formatter={(val) => [fmtMxn(val), "MXN"]} labelFormatter={(l) => `Fecha ${l}`} />
              <Line type="monotone" dataKey="mxn" stroke="#DC5A0F" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top programs */}
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="p-5 rounded-xl border border-gray-800 bg-[#111827]">
          <h3 className="text-sm font-bold text-white mb-3">Top 5 programas · revenue</h3>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-gray-500">
              <tr>
                <th className="text-left py-1">Programa</th>
                <th className="text-right">Ventas</th>
                <th className="text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {p.top_5.length === 0 && <tr><td colSpan={3} className="text-center text-gray-500 py-3">Sin ventas.</td></tr>}
              {p.top_5.map((pr) => (
                <tr key={pr.slug} className="border-t border-gray-800">
                  <td className="py-1 text-white text-xs">{pr.title}</td>
                  <td className="text-right py-1 text-gray-300 font-mono tabular-nums">{fmtNum(pr.enrollments)}</td>
                  <td className="text-right py-1 text-primary font-bold font-mono tabular-nums">{fmtMxn(pr.revenue_mxn)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="p-5 rounded-xl border border-gray-800 bg-[#111827]">
          <h3 className="text-sm font-bold text-white mb-3">Revenue por tenant</h3>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-gray-500">
              <tr>
                <th className="text-left py-1">Tenant</th>
                <th className="text-right">Revenue</th>
                <th className="text-right">Share</th>
              </tr>
            </thead>
            <tbody>
              {part.revenue_by_tenant.length === 0 && <tr><td colSpan={3} className="text-center text-gray-500 py-3">Sin datos.</td></tr>}
              {part.revenue_by_tenant.map((t) => (
                <tr key={t.slug} className="border-t border-gray-800">
                  <td className="py-1 text-white text-xs">{t.name}</td>
                  <td className="text-right py-1 text-primary font-bold font-mono tabular-nums">{fmtMxn(t.mxn)}</td>
                  <td className="text-right py-1 text-gray-400 font-mono tabular-nums">{t.share_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// REVENUE
// =========================================================================
function RevenueTab({ range }) {
  const [groupBy, setGroupBy] = useState("day");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/revenue?range=${range}&group_by=${groupBy}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [range, groupBy]);
  if (loading || !data) return <Skeleton />;

  const isTemporal = ["day", "week", "month"].includes(groupBy);
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-widest text-gray-500 font-bold">Agrupar por</span>
        {["day", "week", "month", "tenant", "program", "source"].map((g) => (
          <button
            key={g}
            onClick={() => setGroupBy(g)}
            className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest border ${
              groupBy === g ? "bg-primary text-primary-foreground border-primary" : "border-gray-800 text-gray-400 hover:bg-white/5"
            }`}
          >
            {g}
          </button>
        ))}
        <div className="flex-1"></div>
        <span className="text-xs text-gray-400">Total: <strong className="text-primary font-mono tabular-nums">{fmtMxn(data.total_mxn)}</strong> · {fmtNum(data.order_count)} orders</span>
      </div>

      {isTemporal && (
        <div className="p-5 rounded-xl border border-gray-800 bg-[#111827]">
          <h3 className="text-sm font-bold text-white mb-3">Serie temporal</h3>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={data.series} margin={CHART_MARGIN}>
                <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                <XAxis dataKey="bucket" stroke="#6b7280" style={{ fontSize: 11 }} />
                <YAxis stroke="#6b7280" style={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <Tooltip contentStyle={{ background: "#0B0F14", border: "1px solid #1f2937", color: "#fff", fontSize: 12 }} formatter={(v) => fmtMxn(v)} />
                <Bar dataKey="mxn" fill="#DC5A0F" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {!isTemporal && (
        <div className="p-5 rounded-xl border border-gray-800 bg-[#111827]">
          <h3 className="text-sm font-bold text-white mb-3">Breakdown por {groupBy}</h3>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <BarChart data={data.breakdown} layout="vertical" margin={{ ...CHART_MARGIN, left: 120 }}>
                <CartesianGrid stroke="#1f2937" strokeDasharray="3 3" />
                <XAxis type="number" stroke="#6b7280" style={{ fontSize: 11 }} tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" stroke="#9ca3af" style={{ fontSize: 11 }} width={120} />
                <Tooltip contentStyle={{ background: "#0B0F14", border: "1px solid #1f2937", color: "#fff", fontSize: 12 }} formatter={(v) => fmtMxn(v)} />
                <Bar dataKey="mxn" fill="#DC5A0F" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="overflow-x-auto border border-gray-800 rounded-xl">
        <table className="w-full text-sm">
          <thead className="bg-[#0B0F14] text-xs uppercase tracking-widest text-gray-400">
            <tr>
              <th className="text-left px-3 py-2">{isTemporal ? "Periodo" : "Dimensión"}</th>
              {!isTemporal && <th className="text-left px-3 py-2">Orders</th>}
              <th className="text-right px-3 py-2">Revenue MXN</th>
              {!isTemporal && <th className="text-right px-3 py-2">Share</th>}
            </tr>
          </thead>
          <tbody>
            {(isTemporal ? data.series : data.breakdown).map((row, i) => (
              <tr key={i} className="border-t border-gray-800 hover:bg-white/5">
                <td className="px-3 py-2 text-white text-xs">{row.bucket || row.name || row.key}</td>
                {!isTemporal && <td className="px-3 py-2 text-gray-300 font-mono">{row.orders}</td>}
                <td className="px-3 py-2 text-right text-primary font-bold font-mono tabular-nums">{fmtMxn(row.mxn)}</td>
                {!isTemporal && <td className="px-3 py-2 text-right text-gray-400 font-mono tabular-nums">{row.share_pct}%</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// =========================================================================
// COHORTS
// =========================================================================
function CohortsTab() {
  const [type, setType] = useState("signup");
  const [metric, setMetric] = useState("retention");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/cohorts?type=${type}&metric=${metric}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [type, metric]);
  if (loading || !data) return <Skeleton />;

  const maxCols = data.matrix.reduce((mx, r) => Math.max(mx, r.cells.length), 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-widest text-gray-500 font-bold">Tipo cohort</span>
        {["signup", "first_purchase"].map((g) => (
          <button key={g} onClick={() => setType(g)}
            className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest border ${
              type === g ? "bg-primary text-primary-foreground border-primary" : "border-gray-800 text-gray-400 hover:bg-white/5"
            }`}>{g}</button>
        ))}
        <span className="mx-3 text-gray-600">·</span>
        <span className="text-xs uppercase tracking-widest text-gray-500 font-bold">Métrica</span>
        {["retention", "revenue"].map((g) => (
          <button key={g} onClick={() => setMetric(g)}
            className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest border ${
              metric === g ? "bg-primary text-primary-foreground border-primary" : "border-gray-800 text-gray-400 hover:bg-white/5"
            }`}>{g}</button>
        ))}
      </div>

      <div className="p-5 rounded-xl border border-gray-800 bg-[#111827] overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="text-xs uppercase tracking-widest text-gray-500">
            <tr>
              <th className="text-left px-2 py-2 sticky left-0 bg-[#111827]">Cohort</th>
              <th className="text-right px-2 py-2">Size</th>
              {Array.from({ length: maxCols }).map((_, i) => (
                <th key={i} className="text-right px-2 py-2">M{i}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.matrix.map((row) => (
              <tr key={row.cohort} className="border-t border-gray-800">
                <td className="px-2 py-2 text-white font-mono sticky left-0 bg-[#111827]">{row.cohort}</td>
                <td className="text-right px-2 py-2 text-gray-300 font-mono">{row.size}</td>
                {Array.from({ length: maxCols }).map((_, i) => {
                  const cell = row.cells[i];
                  if (!cell) return <td key={i} className="px-2 py-2"></td>;
                  const isPct = metric === "retention";
                  const value = cell.value;
                  const intensity = isPct
                    ? Math.min(1, value / 100)
                    : Math.min(1, value / 50000);
                  const bg = `rgba(220, 90, 15, ${intensity.toFixed(2)})`;
                  const label = isPct ? `${value}%` : fmtMxn(value);
                  return (
                    <td key={i} className="text-right px-2 py-2 font-mono tabular-nums text-white" style={{ backgroundColor: bg }}>
                      {label}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {data.matrix.length === 0 && <p className="text-center text-gray-500 py-6">Sin cohorts en el rango.</p>}
      </div>

      <p className="text-[11px] text-gray-500 italic">
        Fórmula retention: usuarios activos (con orden/enrollment) en el mes N ÷ total del cohort M0 × 100.
      </p>
    </div>
  );
}

// =========================================================================
// LTV
// =========================================================================
function LTVTab() {
  const [segment, setSegment] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/ltv?segment=${segment}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [segment]);
  if (loading || !data) return <Skeleton />;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs uppercase tracking-widest text-gray-500 font-bold">Segmento</span>
        {["all", "subscribers", "one_time_buyers"].map((g) => (
          <button key={g} onClick={() => setSegment(g)}
            className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-widest border ${
              segment === g ? "bg-primary text-primary-foreground border-primary" : "border-gray-800 text-gray-400 hover:bg-white/5"
            }`}>{g}</button>
        ))}
      </div>

      <div className="p-8 rounded-xl border border-primary/30 bg-primary/5 text-center">
        <p className="text-xs uppercase tracking-widest text-primary font-bold">LTV promedio · segmento {segment}</p>
        <p className="mt-3 text-5xl font-extrabold text-white tabular-nums">{fmtMxn(data.avg_ltv_mxn)}</p>
        <p className="mt-2 text-sm text-gray-400">
          Muestra: <strong>{data.sample_size}</strong> usuarios · Mediana <strong className="text-white">{fmtMxn(data.median_ltv_mxn)}</strong>
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard label="Max LTV" value={fmtMxn(data.max_ltv_mxn)} icon={TrendingUp} />
        <KpiCard label="Min LTV" value={fmtMxn(data.min_ltv_mxn)} icon={TrendingUp} />
        <KpiCard label="CAC asumido" value={fmtMxn(data.cac_assumed_mxn)} icon={DollarSign} tooltip="Costo asumido de adquisición para calcular payback." />
        <KpiCard label="Payback period" value={`${data.payback_period_days}d`} icon={CreditCard} tooltip="Días estimados para recuperar el CAC." />
      </div>

      <div className="p-5 rounded-xl border border-gray-800 bg-[#111827]">
        <h3 className="text-sm font-bold text-white mb-3">LTV por fuente</h3>
        <div className="space-y-2">
          {Object.entries(data.ltv_by_source_mxn || {}).map(([k, v]) => (
            <div key={k} className="flex items-center justify-between text-sm">
              <span className="text-gray-400 capitalize">{k}</span>
              <span className="text-primary font-bold font-mono tabular-nums">{fmtMxn(v)}</span>
            </div>
          ))}
        </div>
        <p className="mt-4 text-[11px] text-gray-500 italic">
          Metodología: LTV = revenue histórico total por usuario (paid orders + subscripciones). CAC de referencia es un placeholder ($1,500 MXN) — reemplazar con datos reales de marketing en Fase 14.4.
        </p>
      </div>
    </div>
  );
}

// =========================================================================
// FUNNEL
// =========================================================================
function FunnelTab({ range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/funnel?range=${range}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [range]);
  if (loading || !data) return <Skeleton />;

  const funnelData = data.steps.map((s) => ({ name: s.label, value: s.count, key: s.key }));

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="p-5 rounded-xl border border-gray-800 bg-[#111827]">
          <h3 className="text-sm font-bold text-white mb-3">Funnel visitas → orden pagada</h3>
          <div style={{ width: "100%", height: 320 }}>
            <ResponsiveContainer>
              <FunnelChart>
                <Tooltip contentStyle={{ background: "#0B0F14", border: "1px solid #1f2937", color: "#fff", fontSize: 12 }} />
                <Funnel dataKey="value" data={funnelData} isAnimationActive fill="#DC5A0F">
                  <LabelList position="right" fill="#fff" stroke="none" fontSize={11} dataKey="name" />
                  {funnelData.map((_, i) => (
                    <Cell key={i} fill={i === 0 ? "#6B2905" : i === funnelData.length - 1 ? "#DC5A0F" : "#B84A0C"} />
                  ))}
                </Funnel>
              </FunnelChart>
            </ResponsiveContainer>
          </div>
          <p className="mt-2 text-center text-xs text-gray-400">
            Conversión visita → orden pagada: <strong className="text-primary">{data.top_conversion_pct}%</strong>
          </p>
        </div>

        <div className="p-5 rounded-xl border border-gray-800 bg-[#111827]">
          <h3 className="text-sm font-bold text-white mb-3">Detalle por etapa</h3>
          <table className="w-full text-sm">
            <thead className="text-xs uppercase tracking-widest text-gray-500">
              <tr>
                <th className="text-left py-1">Etapa</th>
                <th className="text-right">Count</th>
                <th className="text-right">Conv anterior</th>
              </tr>
            </thead>
            <tbody>
              {data.steps.map((s, i) => (
                <tr key={s.key} className="border-t border-gray-800">
                  <td className="py-2 text-white text-xs">{s.label}</td>
                  <td className="text-right py-2 text-gray-300 font-mono tabular-nums">{fmtNum(s.count)}</td>
                  <td className="text-right py-2 text-primary font-bold font-mono tabular-nums">
                    {i > 0 ? `${s.conversion_from_prev_pct}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <p className="text-[11px] text-gray-500 italic">
        Nota: "Visitas" es un mock (carts × 8). En Fase 14.2 (Analytics events) reemplazamos por tracking real de page_view.
      </p>
    </div>
  );
}

// =========================================================================
// RETENTION (heatmap simplificado)
// =========================================================================
function RetentionTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get(`/admin/reports/retention?range=180d`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);
  if (loading || !data) return <Skeleton />;
  const maxCols = data.matrix.reduce((mx, r) => Math.max(mx, r.cells.length), 0);
  return (
    <div className="space-y-4">
      <div className="p-5 rounded-xl border border-gray-800 bg-[#111827] overflow-x-auto">
        <h3 className="text-sm font-bold text-white mb-3">Heatmap retención · signup cohort</h3>
        <table className="w-full text-xs">
          <thead className="text-xs uppercase tracking-widest text-gray-500">
            <tr>
              <th className="text-left px-2 py-2 sticky left-0 bg-[#111827]">Mes creación</th>
              <th className="text-right px-2 py-2">Cohort</th>
              {Array.from({ length: maxCols }).map((_, i) => (
                <th key={i} className="text-right px-2 py-2">M{i}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.matrix.map((row) => (
              <tr key={row.cohort} className="border-t border-gray-800">
                <td className="px-2 py-2 text-white font-mono sticky left-0 bg-[#111827]">{row.cohort}</td>
                <td className="text-right px-2 py-2 text-gray-300 font-mono">{row.size}</td>
                {Array.from({ length: maxCols }).map((_, i) => {
                  const cell = row.cells[i];
                  if (!cell) return <td key={i} className="px-2 py-2"></td>;
                  const intensity = Math.min(1, cell.value / 100);
                  return (
                    <td key={i} className="text-right px-2 py-2 font-mono tabular-nums text-white"
                      style={{ backgroundColor: `rgba(220, 90, 15, ${intensity.toFixed(2)})` }}>
                      {cell.value}%
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        {data.matrix.length === 0 && <p className="text-center text-gray-500 py-6">Sin datos.</p>}
      </div>
    </div>
  );
}

// =========================================================================
// COMPONENTES REUSABLES
// =========================================================================
function KpiCard({ label, value, sub, delta, icon: Icon, testid, accent, tooltip }) {
  return (
    <div className="p-4 rounded-xl border border-gray-800 bg-[#111827] relative" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-gray-500 font-semibold">
          {Icon && <Icon className="w-3 h-3" />} {label}
        </div>
        {tooltip && (
          <div className="group relative" title={tooltip}>
            <Info className="w-3 h-3 text-gray-500 hover:text-primary cursor-help" />
          </div>
        )}
      </div>
      <p className={`mt-2 text-2xl font-extrabold tabular-nums ${accent ? "text-primary" : "text-white"} truncate`}>
        {value}
      </p>
      {sub && <p className="text-[10px] text-gray-500 mt-1 truncate">{sub}</p>}
      {delta !== undefined && (
        <p className={`text-[10px] mt-1 font-bold ${pctColor(delta)}`}>
          {delta > 0 ? "▲" : delta < 0 ? "▼" : "—"} {delta}% vs periodo anterior
        </p>
      )}
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[...Array(5)].map((_, i) => (
          <div key={i} className="p-4 rounded-xl border border-gray-800 bg-[#111827] h-24 dcc-skeleton" />
        ))}
      </div>
      <div className="p-5 rounded-xl border border-gray-800 bg-[#111827] h-64 dcc-skeleton" />
    </div>
  );
}
